import {
  ExtAdapter,
  ExtFrameMessage,
  ExtInteractingError,
  extMark,
  ExtNotSupportError,
  ExtParamInvalidError,
  HandleFrameMessageResp,
  HandleType,
  ShowFlag,
} from '@hyext-inner/ext-web-comp'
import md5 from 'md5'
import { BusType, EventName, HyPostMessageTopic } from '../../../util/hy'
import { delay } from '../../../util/promise'
import { styleToSelector } from '../../../util/style'
import HyExtAdapterInLiveRoom from '../../adapters/HyExtAdapterInLiveRoom'
import HyExtEndpointModule from '../HyExtEndpointModule'
import { debugLog } from '../../../util/shared'
import querystring from 'querystring'

type PopupMode = 'NORMAL' | 'NOTICE'

type Manager = {
  popupMode: PopupMode
  enter?: (
    from: Manager,
    popupModule: HyExtWebPopupEndpointModule,
    adapter: ExtAdapter,
    req: SetModeReq
  ) => Promise<{
    noticeKey?: string
    showTime?: number
    reportParams?: { [key: string]: string }
  }>
  exit?: (
    to: Manager,
    popupModule: HyExtWebPopupEndpointModule,
    adapter: ExtAdapter,
    req: SetModeReq
  ) => Promise<void>
  reposition: (
    popupModule: HyExtWebPopupEndpointModule,
    adapter: ExtAdapter
  ) => Promise<void>
  setLayout: (
    popupModule: HyExtWebPopupEndpointModule,
    req: SetLayoutReq,
    adapter: ExtAdapter
  ) => Promise<void>
  clear: (
    popupModule: HyExtWebPopupEndpointModule,
    adapter: ExtAdapter
  ) => Promise<void>
}

const normalManager: Manager = (() => {
  const layoutMap: {
    [key: string]: {
      x: number
      y: number
      width: number
      height: number
      visible: boolean
      alpha: number
    }
  } = {}

  const enter = async (
    from: Manager,
    popupModule: HyExtWebPopupEndpointModule,
    adapter: ExtAdapter
  ) => {
    if (from.popupMode === 'NOTICE') {
      await setLayout(
        popupModule,
        {
          ...layoutMap[popupModule.id],
          visible: false,
        },
        adapter
      )
      return {}
    }
  }

  const setLayout = async (
    popupModule: HyExtWebPopupEndpointModule,
    req: SetLayoutReq,
    adapter: ExtAdapter
  ) => {
    layoutMap[popupModule.id] = {
      ...(layoutMap[popupModule.id] || {}),
      ...req,
    }
    reposition(popupModule, adapter)
  }

  const reposition = async (
    popupModule: HyExtWebPopupEndpointModule,
    adapter: ExtAdapter
  ) => {
    const { x, y, width, height, visible, alpha } = {
      x: 0,
      y: 0,
      width: 0,
      height: 0,
      alpha: 1,
      visible: false,
      ...(layoutMap[popupModule.id] || {}),
    }
    const refWidth = popupModule.$ref.width()
    const refHeight = popupModule.$ref.height()
    if (visible) {
      if (popupModule.extEndpoint.typeAuthority.popupContainer === 1) {
        ;(adapter as HyExtAdapterInLiveRoom).chatroomPanelExtSwitch({
          switch: true,
        })
        await delay(100)
      }
      const refOffset = popupModule.$ref.offset() || { top: 0, left: 0 }
      let popUpWidth = Math.round(refWidth * Math.min(1, Math.max(0, width)))
      popUpWidth -= popUpWidth % 2
      let popUpHeight = Math.round(refHeight * Math.min(1, Math.max(0, height)))
      popUpHeight -= popUpHeight % 2
      popupModule.$popup.width(popUpWidth)
      popupModule.$popup.height(popUpHeight)
      popupModule.showPanel(adapter)
      popupModule.$popup.offset({
        top: refOffset.top + refHeight * Math.min(1, Math.max(0, y)),
        left: refOffset.left + refWidth * Math.min(1, Math.max(0, x)),
      })
      popupModule.$popup.css({
        opacity: Math.max(0, Math.min(1, alpha)),
      })
    } else {
      popupModule.hidePanel(adapter)
    }
    return Promise.resolve()
  }

  return {
    popupMode: 'NORMAL' as const,
    setLayout,
    reposition,
    enter,
    clear: async () => {},
  }
})()

const noticeManager: Manager = (() => {
  const width = 255
  const height = 64
  const left = 8
  const bottom = 50

  const popupInfoMap: {
    [liveroomPopupKey: string]: {
      noticeKey: string
      handler: (notice: { status: 'canShow' | 'shouldHide' }) => void
      popupModule: HyExtWebPopupEndpointModule
      containerHeight?: number
    }
  } = {}

  const show = (
    adapter: ExtAdapter,
    popupModule: HyExtWebPopupEndpointModule,
    containerHeight?: number,
    onClose?: () => void,
    showTime?: number
  ) => {
    const acctualHeight = containerHeight || height

    popupModule.$popup.css({
      left,
      top: popupModule.$ref.height() - acctualHeight - bottom,
      width,
      height: acctualHeight,
      display: 'block',
    })

    if (showTime && onClose) {
      // 加入关闭按钮
      const $close = $(
        (adapter as HyExtAdapterInLiveRoom).getPopupCloseBtn(showTime).div
      )
      $close.css({
        position: 'absolute',
        right: -12,
        top: -12,
        left: 'auto',
      })
      $close.on('click', onClose)

      debugLog('【弹窗通道】加入关闭按钮')

      popupModule.$popup.append($close)
    }

    popupModule.showPanel(adapter)
  }

  const hide = (
    popupModule: HyExtWebPopupEndpointModule,
    adapter: ExtAdapter,
    liveroomPopupKey: string,
    isManual?: boolean
  ) => {
    if (Object.keys(popupInfoMap).includes(liveroomPopupKey)) {
      debugLog('【弹窗通道】退组隐藏，清空当前noticeKey', {
        extUuid: popupModule.extMain.extUuid,
        extName: popupModule.extMain.extName,
        liveroomPopupKey,
        isManual: !!isManual,
        noticeKey: popupInfoMap[liveroomPopupKey].noticeKey,
      })

      // 隐藏
      popupModule.hidePanel(adapter)

      // 清除关闭按钮
      popupModule.$popup.find('.popup-close-btn').remove()

      // 取消监听
      popupModule.bus
        .getBus(BusType.POPUP_STATUS)
        .removeListener(
          EventName.POPUP_STATUS_CHANGE,
          popupInfoMap[liveroomPopupKey].handler
        )

      // 清空当前的noticeKey
      delete popupInfoMap[liveroomPopupKey]

      if (isManual) {
        debugLog('【弹窗通道】手动退出，调用TTP.popupDeQueue')
        // 调用出队
        const hyExtAdapterInLiveRoom = adapter as HyExtAdapterInLiveRoom
        hyExtAdapterInLiveRoom.popupDeQueue(liveroomPopupKey)
      }
    } else {
      debugLog(
        `【弹窗通道】liveroomPopupKey:${liveroomPopupKey}已经隐藏过了，忽略`
      )
    }
  }

  const enter = async (
    from: Manager,
    popupModule: HyExtWebPopupEndpointModule,
    adapter: ExtAdapter,
    req: SetModeReq
  ) => {
    if (Object.keys(popupInfoMap).includes(req.liveroomPopupKey)) {
      // 当前已经有入队的noticeKey了，不允许再次入队
      return Promise.reject(new ExtInteractingError('不允许重复入队'))
    }

    // 入队
    const hyExtAdapterInLiveRoom = adapter as HyExtAdapterInLiveRoom
    const enqueueRsp = await hyExtAdapterInLiveRoom.popupEnqueue(
      req.liveroomPopupKey,
      req.showTime,
      req.additionReportParams
    )

    debugLog('【弹窗通道】enqueueRsp', enqueueRsp)

    // 监听队列变化
    popupInfoMap[req.liveroomPopupKey] = {
      popupModule,
      noticeKey: enqueueRsp.noticeKey,
      containerHeight: req.containerHeight,
      handler: (notice: {
        noticeKey: string
        status: 'canShow' | 'shouldHide'
      }) => {
        if (notice.noticeKey === enqueueRsp.noticeKey) {
          debugLog('【弹窗通道】noticeStatusChange触发，匹配上noticeKey', {
            notice,
            extUuid: popupModule.extMain.extUuid,
            extName: popupModule.extMain.extName,
          })

          if (notice.status === 'canShow') {
            // 显示
            show(
              adapter,
              popupModule,
              req.containerHeight,
              () => {
                // 点击关闭按钮
                debugLog('【弹窗通道】点击关闭按钮，退组隐藏')
                hide(popupModule, adapter, req.liveroomPopupKey, true)
              },
              enqueueRsp.showTime
            )
          } else {
            // 隐藏
            hide(popupModule, adapter, req.liveroomPopupKey, false)
          }
        }
      },
    }

    popupModule.bus
      .getBus(BusType.POPUP_STATUS)
      .addListener(
        EventName.POPUP_STATUS_CHANGE,
        popupInfoMap[req.liveroomPopupKey].handler
      )

    // 先隐藏，等消息触发之后再显示
    popupModule.hidePanel(adapter)

    return enqueueRsp
  }

  const exit = async (
    to: Manager,
    popupModule: HyExtWebPopupEndpointModule,
    adapter: ExtAdapter,
    req: SetModeReq
  ) => {
    hide(
      popupModule,
      adapter,
      // 兜底，防止业务setNormal时没有传入liveroomPopupKey
      req.liveroomPopupKey ||
        Object.keys(popupInfoMap).find(
          (k) => popupInfoMap[k].popupModule === popupModule
        ) ||
        '',
      true
    )
  }

  const setLayout = (
    popupModule: HyExtWebPopupEndpointModule,
    req: SetLayoutReq,
    adapter: ExtAdapter
  ) => {
    return Promise.reject(
      new Error('提示模式下不能setLayout，请先退回普通模式')
    )
  }

  const reposition = async (
    popupModule: HyExtWebPopupEndpointModule,
    adapter: ExtAdapter
  ) => {
    const liveroomPopupKey = Object.keys(popupInfoMap).find(
      (k) => popupInfoMap[k].popupModule === popupModule
    )
    if (liveroomPopupKey) {
      show(adapter, popupModule, popupInfoMap[liveroomPopupKey].containerHeight)
    }
  }

  const clear = async (
    popupModule: HyExtWebPopupEndpointModule,
    adapter: ExtAdapter
  ) => {
    Object.keys(popupInfoMap).forEach((k) => {
      if (popupInfoMap[k].popupModule === popupModule) {
        debugLog('【弹窗通道】清空popupModule', {
          extUuid: popupModule.extMain.extUuid,
          extName: popupModule.extMain.extName,
        })
        hide(popupInfoMap[k].popupModule, adapter, k, true)
      }
    })
  }

  return {
    popupMode: 'NOTICE' as const,
    hide,
    enter,
    exit,
    setLayout,
    reposition,
    clear,
  }
})()

const managerMap: { [key in PopupMode]: Manager } = {
  NORMAL: normalManager,
  NOTICE: noticeManager,
}

export default class HyExtWebPopupEndpointModule extends HyExtEndpointModule {
  id: string = md5(String(Math.random()))
  style: any
  selector: any
  $popup: any
  $ref: any
  screenWidth: number = 0
  screenHeight: number = 0
  showed: boolean = false
  modeManager: Manager
  gesturePenetrationMouseMoveHandler: (e: MouseEvent) => void = null
  _disposaled: boolean
  // 业务方法
  async reposition(adapter: ExtAdapter): Promise<void> {
    this.modeManager.reposition(this, adapter)
  }
  clean(adapter: ExtAdapter) {
    if (this.modeManager) {
      debugLog(
        `【浮窗】【${this.extMain.extUuid}】【${this.extEndpoint.extType}】【${this.extMain.extName}】【clean】清理模式管理器`
      )
      this.modeManager.clear(this, adapter)
    }

    debugLog(
      `【清理】【${this.extMain.extUuid}】【${this.extEndpoint.extType}】【${this.extMain.extName}】清除消息通道监听`
    )
    this.bus.clean(
      this.extMain.extUuid,
      this.extMain.extName,
      this.extEndpoint.extType
    )
  }
  unloadPopup(adapter: ExtAdapter) {
    debugLog(
      `【浮窗】【${this.extMain.extUuid}】【${this.extEndpoint.extType}】【${this.extMain.extName}】【unloadPopup】卸载`
    )

    this._disposaled = true
    this.extFrame.unload(adapter)

    this.clean(adapter)
  }
  loadPopup(adapter: ExtAdapter) {
    debugLog(
      `【浮窗】【${this.extMain.extUuid}】【${this.extEndpoint.extType}】【${this.extMain.extName}】【loadPopup】加载`
    )

    this._disposaled = false
    this.emit('postInitResolved')
    this.extFrame.load(adapter)
  }
  handleContextResize(adapter: ExtAdapter) {
    const screenWidth = this.$ref.width()
    const screenHeight = this.$ref.height()
    if (
      this.screenWidth !== screenWidth ||
      this.screenHeight !== screenHeight
    ) {
      this.bus.getBus(BusType.LAYOUT).emit(EventName.LAYOUT_CHANGE_NOTICE, {
        screenWidth,
        screenHeight,
      })
      this.reposition(adapter)
    }
    this.screenWidth = screenWidth
    this.screenHeight = screenHeight
  }
  // SDK方法
  setLayout(req: SetLayoutReq, adapter: ExtAdapter): Promise<void> {
    if (this._disposaled) {
      return Promise.resolve()
    } else {
      return this.modeManager.setLayout(this, req, adapter)
    }
  }
  onLayoutChange(req: OnLayoutChangeReq, adapter: ExtAdapter): Promise<void> {
    const { target } = req
    this.bus.on(
      BusType.LAYOUT,
      EventName.LAYOUT_CHANGE_NOTICE,
      (notice) => {
        const { screenWidth, screenHeight } = notice
        this.extFrame.postMessageToBusiFrame(
          target,
          {
            screenWidth,
            screenHeight,
            isLandscape: true,
          },
          adapter
        )
      },
      true
    )
    // TODO 调用onLayoutChange固定触发一次
    setTimeout(
      () =>
        this.bus.getBus(BusType.LAYOUT).emit(EventName.LAYOUT_CHANGE_NOTICE, {
          screenWidth: this.$ref.width(),
          screenHeight: this.$ref.height(),
        }),
      100
    )
    return Promise.resolve()
  }
  // 覆盖方法
  startup(adapter: ExtAdapter) {
    if (this.hbTimer === 0) {
      this.sys_pageshow_ext_panelshow(adapter)
    }
    super.startup(adapter)
  }
  // 抽象方法
  async initEndpoint(adapter: ExtAdapter): Promise<void> {
    this.modeManager = normalManager
    const hyExtAdapter = adapter as HyExtAdapterInLiveRoom

    if (this.extEndpoint.typeAuthority) {
      switch (this.extEndpoint.typeAuthority.popupContainer) {
        case 1:
          this.$ref = $('#chatRoom')
          break
        case 2: {
          const player = await hyExtAdapter.getPlayer()
          this.$ref = player.getVideoContainer()
          break
        }
        default:
          this.$ref = $('html')
          break
      }
    } else {
      this.$ref = $('html')
    }

    this.style = require('@/sass/popup.scss')
    this.selector = styleToSelector(this.style)
    this.$popup = $(
      require('@/tpl/popup.html')({
        style: this.style,
        extUuid: this.extMain.extUuid,
        showFlag: this.extEndpoint.typeAuthority.extTypeIconInfo.showFlag,
      })
    )
    this.$popup.css({ 'z-index': this.extEndpoint.typeAuthority.weight })
    if (this.extEndpoint.extConfig) {
      let { width, height } = this.extEndpoint.typeAuthority
      if (!isNaN(width) && width) {
        width = width - (width % 2)
        this.$popup.css({ width })
      }
      if (!isNaN(height) && height) {
        height = height - (height % 2)
        this.$popup.css({ height })
      }
    }

    // 监听容器大小变化
    if (
      this.extEndpoint.typeAuthority &&
      this.extEndpoint.typeAuthority.popupContainer === 2
    ) {
      hyExtAdapter.onPageFullScreen(() => this.handleContextResize(adapter))
    }
    $(window).on('resize', () => this.handleContextResize(adapter))

    return Promise.resolve()
  }
  async appendFrame(adapter: ExtAdapter) {
    if (this.$popup.find('section').has(this.extFrame.iframe).length > 0) {
      debugLog(
        `【浮窗】【${this.extMain.extUuid}】【${this.extEndpoint.extType}】【${this.extMain.extName}】容器里面有ifrme，表示已经初始化过了，忽略`
      )
      return
    }

    extMark(
      'load_supframe_start',
      this.extMain.extUuid,
      this.extEndpoint.extType
    )

    const hyExtAdapter = adapter as HyExtAdapterInLiveRoom
    if (this.extEndpoint.typeAuthority) {
      switch (this.extEndpoint.typeAuthority.popupContainer) {
        case 1:
          $('#wrap-ext').prepend(this.$popup)
          break
        case 2: {
          let $container
          if (this.extEndpoint.typeAuthority.popupLevel < 300) {
            $container = $(hyExtAdapter.getExtContainerUnderBarrage())
          } else {
            $container = $(hyExtAdapter.getExtPopupContainer())
          }
          $container.prepend(this.$popup)
          break
        }
        default:
          $('body').append(this.$popup)
          break
      }
    } else {
      $('body').append(this.$popup)
    }

    debugLog(
      `【浮窗】【${this.extMain.extUuid}】【${this.extEndpoint.extType}】【${this.extMain.extName}】加入容器`
    )

    this.$popup.find('section').append(this.extFrame.iframe)

    debugLog(
      `【浮窗】【${this.extMain.extUuid}】【${this.extEndpoint.extType}】【${this.extMain.extName}】加入iframe`
    )

    return new Promise<void>((resolve) => {
      const handler = () => {
        debugLog(
          `【浮窗】【${this.extMain.extUuid}】【${this.extEndpoint.extType}】【${this.extMain.extName}】iframeReady`
        )
        this.reposition(adapter)
        this.sys_pageshow_ext_list(adapter)
        this.sys_startup_ext_interactive(adapter)
        resolve()
      }
      if (!this.extFrame.loaded) {
        this.once('supFrameReady', handler)
      } else {
        handler()
      }
    })
  }
  async statusNotice(
    extTypeIconInfo: MidExtQuery.ExtTypeIconInfo,
    adapter: ExtAdapter
  ): Promise<void> {
    if (extTypeIconInfo) {
      this.extEndpoint.typeAuthority.extTypeIconInfo = extTypeIconInfo
    }
    const { showFlag } = this.extEndpoint.typeAuthority.extTypeIconInfo
    if (showFlag & Number(ShowFlag.PANEL)) {
      debugLog(
        `【浮窗】【${this.extMain.extUuid}】【${this.extEndpoint.extType}】【${this.extMain.extName}】加载`,
        this.extEndpoint.typeAuthority.extTypeIconInfo
      )
      // @ts-expect-error 这里应该有，json结构跟jce结构对不齐
      const param = extTypeIconInfo.param
      const query = param ? querystring.stringify(param) : ''
      this.loadByTrigger(adapter as HyExtAdapterInLiveRoom, query)
    } else {
      debugLog(
        `【浮窗】【${this.extMain.extUuid}】【${this.extEndpoint.extType}】【${
          this.extMain.extName
        }】${this.extFrame.busiFrameLoaded ? '卸载' : '不加载'}`,
        this.extEndpoint.typeAuthority.extTypeIconInfo
      )

      // TODO
      this.$popup.hide()
      this.unloadPopup(adapter)
    }
    return Promise.resolve()
  }
  async postInit() {
    const { showFlag } = this.extEndpoint.typeAuthority.extTypeIconInfo
    if (showFlag & Number(ShowFlag.PANEL)) {
      this.loaded = true
      return Promise.resolve()
    } else {
      return new Promise<void>((resolve) => {
        this.once('postInitResolved', () => resolve())
      })
    }
  }
  showEndpointPanel(adapter: ExtAdapter): Promise<void> {
    // 第一次show的时候才触发onLoad的生命周期
    if (!this.showed) {
      this.showed = true
      this.postLifeLoad(adapter)
      this.postLifeAppear(adapter)
    } else {
      if (!this.$popup.is(':visible')) {
        this.postLifeEnterForeground(adapter)
        this.postLifeAppear(adapter)
      }
    }
    this.$popup.show()
    return Promise.resolve()
  }
  hideEndpointPanel(adapter: ExtAdapter): Promise<void> {
    if (this.$popup.is(':visible')) {
      this.postLifeLeaveForeground(adapter)
      this.postLifeDisappear(adapter)
    }
    this.$popup.hide()
    return Promise.resolve()
  }
  showEndpointEntrance(): Promise<void> {
    return Promise.resolve()
  }
  hideEndpointEntrance(): Promise<void> {
    return Promise.resolve()
  }
  ready(): Promise<boolean> {
    return Promise.resolve(true)
  }
  showSupError(): Promise<void> {
    return Promise.resolve()
  }
  getModalContainer(): Promise<any> {
    return Promise.resolve($('body'))
  }
  textNotice(tips: string): Promise<void> {
    throw new Error('Method not implemented.')
  }
  close(content: string): Promise<void> {
    this.logger.warn('显示下线提示', content)
    this.extFrame.destroy()
    return Promise.resolve()
  }
  async setMode(
    req: SetModeReq,
    adapter: ExtAdapter
  ): Promise<SetModeRsp | void> {
    if (req.mode !== this.modeManager.popupMode) {
      if (Object.keys(managerMap).includes(req.mode)) {
        const prevModeManager = this.modeManager
        this.modeManager = managerMap[req.mode]
        if (prevModeManager.exit) {
          await prevModeManager.exit(this.modeManager, this, adapter, req)
        }
        if (this.modeManager.enter) {
          const enterRsp = await this.modeManager.enter(
            prevModeManager,
            this,
            adapter,
            req
          )
          return enterRsp
        }
      } else {
        return Promise.reject(new ExtNotSupportError(`不支持${req.mode}模式`))
      }
    } else if (req.mode !== 'NORMAL') {
      return Promise.reject(new ExtParamInvalidError('已经处于特殊模式'))
    }
  }
  async handleFrameMessage(
    msg: ExtFrameMessage,
    adapter: ExtAdapter
  ): Promise<HandleFrameMessageResp> {
    if (msg.originData.event === 'setPointerEvents') {
      if (this.gesturePenetrationMouseMoveHandler) {
        const ev = msg.originData.through ? 'none' : 'auto'
        const pointerEvents = this.$popup.css('pointer-events')
        if (pointerEvents !== ev) {
          this.$popup.css('pointer-events', ev)
          this.extFrame.iframe.style.pointerEvents = ev
        }
      }
      return {
        handleType: HandleType.SILENT,
        rsp: null,
        err: null,
      }
    } else {
      return super.handleFrameMessage(msg, adapter)
    }
  }
  async setGesturePenetration(
    req: SetGesturePenetrationReq,
    adapter: ExtAdapter
  ): Promise<void> {
    if (req.enable) {
      this.gesturePenetrationMouseMoveHandler = (e) => {
        if (this.extFrame.busiFrameLoaded) {
          let coord = {
            x: e.pageX - this.extFrame.iframe.getBoundingClientRect().left,
            y: e.pageY - this.extFrame.iframe.getBoundingClientRect().top,
          }
          // 判断当前鼠标是否在iframe中
          // 一旦鼠标进入iframe中，判断target元素是否要响应，iframe的pointerEvents设置为auto，否则iframe接收不到事件
          // 父级页面捕获不到任何事件
          this.extFrame.iframe.contentWindow.postMessage(
            {
              event: 'onMouseMoveOnParent',
              x: coord.x,
              y: coord.y,
            },
            '*'
          )
        }
      }
      document.addEventListener(
        'mousemove',
        this.gesturePenetrationMouseMoveHandler,
        true
      )
    } else {
      if (this.gesturePenetrationMouseMoveHandler) {
        document.removeEventListener(
          'mousemove',
          this.gesturePenetrationMouseMoveHandler,
          true
        )
        this.gesturePenetrationMouseMoveHandler = null
      }
      this.$popup.css('pointer-events', 'auto')
      this.extFrame.iframe.style.pointerEvents = 'auto'
    }
  }
  async disposal(_: any, adapter: ExtAdapter): Promise<void> {
    this._disposaled = true

    this.clean(adapter)

    return super.disposal(_, adapter)
  }
  async onNoticeShow(req: OnNoticeShowReq, adapter: ExtAdapter): Promise<void> {
    this.bus.on(
      BusType.NOTICE_SHOW,
      EventName.NOTICE_SHOW,
      (notice) => {
        this.extFrame.postMessageToBusiFrame(req.target, notice, adapter)
      },
      true
    )
  }
  async offNoticeShow(_: any, adapter: ExtAdapter): Promise<void> {
    this.bus.off(BusType.NOTICE_SHOW, EventName.NOTICE_SHOW)
  }
  async onNoticeHide(req: OnNoticeHideReq, adapter: ExtAdapter): Promise<void> {
    this.bus.on(
      BusType.NOTICE_HIDE,
      EventName.NOTICE_HIDE,
      (notice) => {
        this.extFrame.postMessageToBusiFrame(req.target, notice, adapter)
      },
      true
    )
  }
  async offNoticeHide(_: any, adapter: ExtAdapter): Promise<void> {
    this.bus.off(BusType.NOTICE_HIDE, EventName.NOTICE_HIDE)
  }
  async popupEnQueue(
    req: PopupEnqueueReq,
    adapter: ExtAdapter
  ): Promise<PopupEnqueueRsp> {
    const hyExtAdapterInLiveRoom = adapter as HyExtAdapterInLiveRoom
    return hyExtAdapterInLiveRoom.popupEnqueue(
      req.liveroomPopupKey,
      req.showTime,
      req.additionReportParams
    )
  }
  async popupDeQueue(req: PopupDequeueReq, adapter: ExtAdapter): Promise<void> {
    const hyExtAdapterInLiveRoom = adapter as HyExtAdapterInLiveRoom
    return hyExtAdapterInLiveRoom.popupDeQueue(req.liveroomPopupKey)
  }
  async onPopupStatusChange(
    req: OnPopupStatusChangeReq,
    adapter: ExtAdapter
  ): Promise<void> {
    this.bus.on(
      BusType.POPUP_STATUS,
      EventName.POPUP_STATUS_CHANGE,
      (notice) => {
        this.extFrame.postMessageToBusiFrame(req.target, notice, adapter)
      }
    )
  }
  async offPopupStatusChange(_: any, adapter: ExtAdapter): Promise<void> {
    this.bus.off(BusType.POPUP_STATUS, EventName.POPUP_STATUS_CHANGE)
  }
  loadByTrigger(adapter: HyExtAdapterInLiveRoom, query?: string) {
    if (!this.extFrame.busiFrameLoaded) {
      // 记录参数
      this.query = query
      const handler = () => {
        debugLog(
          `【浮窗】【${this.extMain.extUuid}】【${this.extEndpoint.extType}】【${this.extMain.extName}】加载业务iframe`
        )
        this.loadPopup(adapter)
      }
      if (!this.extFrame.loaded) {
        this.appendFrame(adapter).then(handler)
      } else {
        handler()
      }
    } else {
      let q: any = {}
      try {
        // 优先使用json
        q = JSON.parse(query)
      } catch (err) {
        try {
          q = querystring.parse(query)
        } catch (err) {
          this.logger.warn(
            'loadPopup传入的参数既不是json也不是querystring，忽略',
            { query }
          )
        }
      }
      this.postContextMessage(HyPostMessageTopic.ACTIVATED, q, adapter)
    }
  }
}
